import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

const PRIMARY = "#2563EB"
const SECONDARY = "#D97706"
const PREVIOUS = "#94A3B8"
const GRID = "rgba(100, 116, 139, 0.15)"

// The PPC chart. The page sends the five sums per day (impressions, clicks, orders, spend, sales) for the period and for the
// comparison period; this turns them into days, weeks or months and works every ratio (ACOS, CTR, ...) out from the summed
// numbers, so a week's ACOS is the week's spend over the week's sales, not an average of seven percentages.
// Up to two metrics at a time: the first as bars (or a line, for a rate) on the left axis, the second as a line on the right.
export default class extends Controller {
  static targets = ["canvas", "card", "primary", "secondary", "gran", "compareToggle", "empty"]
  static values = { series: Object, metrics: Array, currency: String, compare: Boolean }

  connect() {
    this.defs = Object.fromEntries(this.metricsValue.map((metric) => [metric.key, metric]))
    const saved = this.load()
    this.primaryKey = this.defs[saved.primary] ? saved.primary : "sales"
    this.secondaryKey = saved.secondary === "" || this.defs[saved.secondary] ? saved.secondary : "acos"
    this.gran = ["day", "week", "month"].includes(saved.gran) ? saved.gran : this.defaultGranularity()
    this.sync()
    this.draw()
  }

  disconnect() {
    this.chart?.destroy()
  }

  // A KPI card was clicked: it becomes the main metric, and the one it replaces moves to the second slot.
  pick(event) {
    const key = event.currentTarget.dataset.metric
    if (key === this.primaryKey) return

    this.secondaryKey = this.primaryKey
    this.primaryKey = key
    this.save()
    this.sync()
    this.draw()
  }

  select() {
    this.primaryKey = this.primaryTarget.value
    this.secondaryKey = this.secondaryTarget.value === this.primaryKey ? "" : this.secondaryTarget.value
    this.save()
    this.sync()
    this.draw()
  }

  granularity(event) {
    this.gran = event.currentTarget.dataset.gran
    this.save()
    this.sync()
    this.draw()
  }

  redraw() {
    this.draw()
  }

  // ---- state -------------------------------------------------------------------------------------------------

  sync() {
    this.primaryTarget.value = this.primaryKey
    this.secondaryTarget.value = this.secondaryKey
    this.cardTargets.forEach((card) => {
      const key = card.dataset.metric
      card.classList.toggle("is-primary", key === this.primaryKey)
      card.classList.toggle("is-secondary", key === this.secondaryKey)
      card.setAttribute("aria-pressed", String(key === this.primaryKey || key === this.secondaryKey))
    })
    this.granTargets.forEach((button) => {
      const active = button.dataset.gran === this.gran
      button.classList.toggle("active", active)
      button.classList.toggle("btn-outline-secondary", !active)
      button.classList.toggle("btn-primary", active)
    })
  }

  defaultGranularity() {
    const days = this.seriesValue.dates.length
    return days > 120 ? "month" : days > 45 ? "week" : "day"
  }

  load() {
    try { return JSON.parse(window.localStorage.getItem("ppc.chart")) || {} } catch (_error) { return {} }
  }

  save() {
    try {
      window.localStorage.setItem("ppc.chart", JSON.stringify({ primary: this.primaryKey, secondary: this.secondaryKey, gran: this.gran }))
    } catch (_error) { /* not remembered */ }
  }

  // ---- data --------------------------------------------------------------------------------------------------

  // Buckets the days into days, weeks (Monday start) or months and sums them; returns the labels and one sums object per bucket.
  bucket(dates, sums) {
    const keyFor = (iso) => {
      if (this.gran === "day") return iso
      const [y, m, d] = iso.split("-").map(Number)
      const date = new Date(Date.UTC(y, m - 1, d))
      if (this.gran === "month") return `${y}-${String(m).padStart(2, "0")}-01`
      const offset = (date.getUTCDay() + 6) % 7
      date.setUTCDate(date.getUTCDate() - offset)
      return date.toISOString().slice(0, 10)
    }
    const order = []
    const buckets = {}
    dates.forEach((iso, index) => {
      const key = keyFor(iso)
      if (!buckets[key]) {
        buckets[key] = { impressions: 0, clicks: 0, orders: 0, spend: 0, sales: 0 }
        order.push(key)
      }
      Object.keys(buckets[key]).forEach((metric) => { buckets[key][metric] += sums[metric][index] || 0 })
    })
    return { labels: order, values: order.map((key) => buckets[key]) }
  }

  derive(sums, key) {
    const { impressions, clicks, orders, spend, sales } = sums
    switch (key) {
      case "acos": return sales > 0 ? (spend / sales) * 100 : null
      case "roas": return spend > 0 ? sales / spend : null
      case "ctr": return impressions > 0 ? (clicks / impressions) * 100 : null
      case "cpc": return clicks > 0 ? spend / clicks : null
      case "cvr": return clicks > 0 ? (orders / clicks) * 100 : null
      default: return sums[key]
    }
  }

  format(key, value, compact = false) {
    if (value === null || value === undefined) return "-"
    const kind = this.defs[key].kind
    if (kind === "percent") return `${value.toFixed(compact ? 0 : 1)}%`
    if (kind === "ratio") return value.toFixed(2)
    if (kind === "money_precise") return `${this.currencyValue}${value.toFixed(2)}`
    const text = compact ? this.compact(value) : Math.round(value).toLocaleString("en-IN")
    return kind === "money" ? `${this.currencyValue}${text}` : text
  }

  compact(value) {
    const abs = Math.abs(value)
    if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`
    if (abs >= 1e3) return `${(value / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`
    return String(Math.round(value))
  }

  label(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    const date = new Date(y, m - 1, d)
    if (this.gran === "month") return date.toLocaleDateString("en-IN", { month: "short", year: "2-digit" })
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })
  }

  // ---- drawing -----------------------------------------------------------------------------------------------

  draw() {
    const series = this.seriesValue
    const current = this.bucket(series.dates, series.cur)
    const showPrevious = this.compareValue && series.prev && (!this.hasCompareToggleTarget || this.compareToggleTarget.checked)
    const previous = showPrevious ? this.bucket(series.compare_dates, series.prev) : null

    const total = current.values.reduce((sum, bucket) => sum + bucket.impressions + bucket.spend, 0)
    this.emptyTarget.hidden = total > 0
    this.canvasTarget.hidden = total === 0
    if (total === 0) { this.chart?.destroy(); this.chart = null; return }

    const keys = [this.primaryKey, this.secondaryKey].filter(Boolean)
    const datasets = []
    keys.forEach((key, index) => {
      const def = this.defs[key]
      const color = index === 0 ? PRIMARY : SECONDARY
      // Counts and money read best as bars; the second metric, and any rate, as a line.
      const asBar = def.additive && index === 0
      datasets.push({
        type: asBar ? "bar" : "line", label: def.label, metricKey: key,
        data: current.values.map((bucket) => this.derive(bucket, key)),
        yAxisID: index === 0 ? "y" : "y1", order: asBar ? 3 : 1,
        backgroundColor: asBar ? `${color}CC` : color, borderColor: color, borderWidth: asBar ? 0 : 2,
        borderRadius: asBar ? 3 : 0, maxBarThickness: 36, tension: 0.25, pointRadius: current.labels.length > 40 ? 0 : 3,
        pointBackgroundColor: color, spanGaps: true
      })
      if (previous && index === 0) {
        datasets.push({
          type: "line", label: `${def.label} (previous)`, metricKey: key, previous: true,
          data: previous.values.slice(0, current.labels.length).map((bucket) => this.derive(bucket, key)),
          yAxisID: "y", order: 2, borderColor: PREVIOUS, borderWidth: 2, borderDash: [6, 4], pointRadius: 0, tension: 0.25, spanGaps: true
        })
      }
    })

    const axis = (key, position, grid) => ({
      position, beginAtZero: true, grid: { drawOnChartArea: grid, color: GRID }, border: { display: false },
      ticks: { callback: (value) => this.format(key, value, true), color: key === this.primaryKey ? PRIMARY : SECONDARY, maxTicksLimit: 6 },
      title: { display: false }
    })
    const options = {
      responsive: true, maintainAspectRatio: false, interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: true, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 }, sort: (a, b) => a.datasetIndex - b.datasetIndex } },
        tooltip: {
          callbacks: {
            title: (items) => {
              const index = items[0].dataIndex
              const start = this.label(current.labels[index])
              return this.gran === "day" ? start : `${this.gran === "week" ? "Week of " : ""}${start}`
            },
            label: (item) => ` ${item.dataset.label}: ${this.format(item.dataset.metricKey, item.parsed.y)}`
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { callback: (_v, index) => this.label(current.labels[index]), maxRotation: 0, autoSkip: true, color: "#64748B" } },
        y: axis(this.primaryKey, "left", true)
      }
    }
    if (this.secondaryKey) options.scales.y1 = axis(this.secondaryKey, "right", false)

    this.chart?.destroy()
    this.chart = new Chart(this.canvasTarget, { type: "bar", data: { labels: current.labels, datasets }, options })
  }
}
